import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApiClient } from '../ApiClientProvider';
import { queryKeys } from '../keys';
import type { PickupLocation, PickupLocationPayload } from '../types/pickups';

type Wrapped<T> = { data: T };

export function usePickupLocations() {
  const api = useApiClient();
  return useQuery<PickupLocation[], Error>({
    queryKey: queryKeys.pickupLocations.list(),
    queryFn: async ({ signal }) => {
      const response = await api.get<Wrapped<PickupLocation[]>>('/admin/pickup-locations', signal);
      return response.data;
    },
  });
}

export function useCreatePickupLocation() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<PickupLocation, Error, PickupLocationPayload>({
    mutationFn: async (payload) => {
      const response = await api.post<Wrapped<PickupLocation>>('/admin/pickup-locations', payload);
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pickupLocations.all });
    },
  });
}

export function useUpdatePickupLocation(id: number) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<PickupLocation, Error, PickupLocationPayload>({
    mutationFn: async (payload) => {
      const response = await api.put<Wrapped<PickupLocation>>(
        `/admin/pickup-locations/${id}`,
        payload,
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pickupLocations.all });
    },
  });
}

export function useDeletePickupLocation() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: async (id) => {
      await api.del(`/admin/pickup-locations/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pickupLocations.all });
    },
  });
}

export function useSetDefaultPickupLocation() {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation<PickupLocation, Error, number>({
    mutationFn: async (id) => {
      const response = await api.post<Wrapped<PickupLocation>>(
        `/admin/pickup-locations/${id}/set-default`,
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.pickupLocations.all });
    },
  });
}
